import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Menu, 
  X, 
  Leaf, 
  TreeDeciduous, 
  Flower2, 
  Scissors,
  CheckCircle2,
  Star,
  Clock,
  Calendar as CalendarIcon,
  ArrowRight,
  ChevronDown
} from "lucide-react";
import { format, addDays, isBefore, startOfDay } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Services data
const services = [
  {
    id: "lawn-mowing",
    title: "Lawn Mowing & Maintenance",
    description: "Regular mowing, edging, and trimming to keep your lawn looking pristine all season long.",
    icon: Leaf,
    features: ["Weekly/Bi-weekly service", "Edge trimming", "Clippings removal", "Seasonal adjustments"]
  },
  {
    id: "landscaping",
    title: "Landscaping & Design",
    description: "Transform your outdoor space with professional landscape design and installation services.",
    icon: Flower2,
    features: ["Custom design", "Plant installation", "Hardscape work", "Drainage solutions"]
  },
  {
    id: "fertilization",
    title: "Fertilization & Weed Control",
    description: "Keep your lawn healthy and weed-free with our comprehensive treatment programs.",
    icon: TreeDeciduous,
    features: ["Soil analysis", "Custom fertilizer", "Weed prevention", "Pest control"]
  },
  {
    id: "trimming",
    title: "Tree & Shrub Trimming",
    description: "Expert pruning and shaping to enhance the beauty and health of your trees and shrubs.",
    icon: Scissors,
    features: ["Seasonal pruning", "Shape maintenance", "Dead branch removal", "Health assessment"]
  }
];

// Time slots
const timeSlots = [
  "8:00 AM", "9:00 AM", "10:00 AM", "11:00 AM",
  "12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM"
];

// Testimonials
const testimonials = [
  {
    name: "Sarah M.",
    location: "Local Homeowner",
    text: "T&V Lawn Care transformed my backyard into a beautiful oasis. Their attention to detail is unmatched!",
    rating: 5
  },
  {
    name: "Mike R.",
    location: "Business Owner",
    text: "Professional, reliable, and always on time. My commercial property has never looked better.",
    rating: 5
  },
  {
    name: "Jennifer L.",
    location: "Local Resident",
    text: "Emmanuel and his team are fantastic! They really care about the quality of their work.",
    rating: 5
  }
];

// Header Component
const Header = ({ activeSection }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: "#services", label: "Services" },
    { href: "#about", label: "About" },
    { href: "#booking", label: "Book Now" },
    { href: "#contact", label: "Contact" }
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'glass shadow-lg' : 'bg-transparent'
      }`}
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#" className="flex items-center gap-3" data-testid="logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_grass-quotes/artifacts/5ya1xfn4_DCCAD81D-0CE1-41B9-A712-38D6B35E8986.png" 
              alt="T&V Lawn Care Co. Logo"
              className="h-14 w-auto"
            />
            <div className={`hidden sm:block ${isScrolled ? 'text-[hsl(80,70%,30%)]' : 'text-white'}`}>
              <h1 className="font-bold text-xl tracking-tight">T&V Lawn Care</h1>
              <p className={`text-xs ${isScrolled ? 'text-gray-600' : 'text-white/80'}`}>Co.</p>
            </div>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className={`nav-link font-medium transition-colors ${
                  isScrolled ? 'text-gray-700 hover:text-[hsl(80,70%,40%)]' : 'text-white/90 hover:text-white'
                }`}
              >
                {link.label}
              </a>
            ))}
            <a href="#estimate">
              <Button 
                className="bg-[hsl(30,60%,50%)] hover:bg-[hsl(30,60%,45%)] text-white rounded-full px-6"
                data-testid="header-estimate-btn"
              >
                Free Estimate
              </Button>
            </a>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {isMenuOpen ? (
              <X className={`w-6 h-6 ${isScrolled ? 'text-gray-800' : 'text-white'}`} />
            ) : (
              <Menu className={`w-6 h-6 ${isScrolled ? 'text-gray-800' : 'text-white'}`} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden mobile-menu-enter glass rounded-2xl mt-2 p-4 mb-4" data-testid="mobile-menu">
            <nav className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-gray-800 font-medium py-2 hover:text-[hsl(80,70%,40%)]"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.label}
                </a>
              ))}
              <a href="#estimate" onClick={() => setIsMenuOpen(false)}>
                <Button className="w-full bg-[hsl(30,60%,50%)] hover:bg-[hsl(30,60%,45%)] text-white rounded-full">
                  Free Estimate
                </Button>
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section 
      className="relative min-h-screen flex items-center hero-bg"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 hero-gradient"></div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="max-w-3xl">
          <div className="animate-fade-in-up">
            <span className="inline-block px-4 py-2 rounded-full bg-white/20 text-white text-sm font-medium mb-6 backdrop-blur-sm">
              Professional Lawn Care Services
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight animate-fade-in-up delay-100">
            Your Lawn Deserves
            <span className="block text-[hsl(45,80%,70%)]">The Best Care</span>
          </h1>
          <p className="mt-6 text-lg text-white/90 max-w-xl animate-fade-in-up delay-200">
            T&V Lawn Care Co. delivers exceptional lawn maintenance, landscaping, and outdoor services. 
            Let us transform your property into the envy of the neighborhood.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 animate-fade-in-up delay-300">
            <a href="#estimate">
              <Button 
                size="lg" 
                className="bg-[hsl(30,60%,50%)] hover:bg-[hsl(30,60%,45%)] text-white rounded-full px-8 py-6 text-lg font-semibold shadow-xl"
                data-testid="hero-estimate-btn"
              >
                Get Free Estimate
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
            <a href="#booking">
              <Button 
                size="lg" 
                variant="outline" 
                className="bg-white/10 backdrop-blur-sm border-white/30 text-white hover:bg-white/20 rounded-full px-8 py-6 text-lg"
                data-testid="hero-booking-btn"
              >
                Book a Service
              </Button>
            </a>
          </div>
          <div className="mt-12 flex items-center gap-8 animate-fade-in-up delay-400">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-[hsl(45,80%,70%)]" />
              <span className="text-white/90 text-sm">Licensed & Insured</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-[hsl(45,80%,70%)]" />
              <span className="text-white/90 text-sm">Free Estimates</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-[hsl(45,80%,70%)]" />
              <span className="text-white/90 text-sm">Satisfaction Guaranteed</span>
            </div>
          </div>
        </div>
      </div>
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <a href="#services">
          <ChevronDown className="w-8 h-8 text-white/70" />
        </a>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  return (
    <section id="services" className="py-24 section-cream" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 rounded-full bg-[hsl(80,60%,90%)] text-[hsl(80,70%,35%)] text-sm font-medium mb-4">
            Our Services
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-[hsl(80,70%,30%)]">
            Complete Lawn Care Solutions
          </h2>
          <p className="mt-4 text-gray-600 max-w-2xl mx-auto">
            From routine maintenance to complete landscape transformations, we've got your outdoor needs covered.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <Card 
              key={service.id} 
              className="service-card bg-white border-0 shadow-lg overflow-hidden"
              data-testid={`service-card-${service.id}`}
            >
              <CardHeader className="pb-4">
                <div className="service-icon mb-4">
                  <service.icon className="w-8 h-8" />
                </div>
                <CardTitle className="text-xl text-[hsl(80,70%,30%)]">{service.title}</CardTitle>
                <CardDescription className="text-gray-600">{service.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                      <CheckCircle2 className="w-4 h-4 text-[hsl(80,70%,45%)]" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" className="py-24 section-white" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="inline-block px-4 py-2 rounded-full bg-[hsl(80,60%,90%)] text-[hsl(80,70%,35%)] text-sm font-medium mb-4">
              About Us
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[hsl(80,70%,30%)] mb-6">
              Dedicated to Making Your Lawn Thrive
            </h2>
            <p className="text-gray-600 mb-6">
              At T&V Lawn Care Co., we believe every property deserves professional attention and care. 
              Owner Emmanuel Villegas brings years of experience and a genuine passion for creating beautiful 
              outdoor spaces.
            </p>
            <p className="text-gray-600 mb-8">
              We pride ourselves on reliability, quality workmanship, and building lasting relationships 
              with our clients. Your satisfaction is our top priority, and we won't rest until your lawn 
              looks its absolute best.
            </p>
            <div className="grid sm:grid-cols-3 gap-6">
              <div className="text-center p-4 rounded-xl bg-[hsl(80,60%,95%)]">
                <div className="text-3xl font-bold text-[hsl(80,70%,35%)]">100+</div>
                <div className="text-sm text-gray-600">Happy Clients</div>
              </div>
              <div className="text-center p-4 rounded-xl bg-[hsl(80,60%,95%)]">
                <div className="text-3xl font-bold text-[hsl(80,70%,35%)]">5+</div>
                <div className="text-sm text-gray-600">Years Experience</div>
              </div>
              <div className="text-center p-4 rounded-xl bg-[hsl(80,60%,95%)]">
                <div className="text-3xl font-bold text-[hsl(80,70%,35%)]">100%</div>
                <div className="text-sm text-gray-600">Satisfaction</div>
              </div>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1558904541-efa843a96f01?w=800&q=80" 
                alt="Beautiful lawn care"
                className="w-full h-[500px] object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-6 max-w-xs">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-[hsl(80,70%,40%)] flex items-center justify-center">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="text-sm text-gray-500">Call Us Today</div>
                  <div className="font-bold text-[hsl(80,70%,40%)]">(940) 232-8411</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Booking Section
const BookingSection = () => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");
  const [bookedSlots, setBookedSlots] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    service_type: "",
    notes: ""
  });

  // Fetch booked slots when date changes
  useEffect(() => {
    if (selectedDate) {
      fetchBookedSlots(format(selectedDate, 'yyyy-MM-dd'));
    }
  }, [selectedDate]);

  const fetchBookedSlots = async (date) => {
    try {
      const response = await axios.get(`${API}/bookings/date/${date}`);
      setBookedSlots(response.data.map(b => b.time_slot));
    } catch (error) {
      console.error("Error fetching bookings:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDate || !selectedTime || !formData.service_type) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`${API}/bookings`, {
        ...formData,
        date: format(selectedDate, 'yyyy-MM-dd'),
        time_slot: selectedTime
      });
      toast.success("Booking confirmed! We'll see you soon.");
      setFormData({ name: "", email: "", phone: "", address: "", service_type: "", notes: "" });
      setSelectedDate(null);
      setSelectedTime("");
    } catch (error) {
      toast.error("Failed to create booking. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const disabledDays = [
    { before: startOfDay(new Date()) },
    { dayOfWeek: [0] } // Disable Sundays
  ];

  return (
    <section id="booking" className="py-24 section-cream" data-testid="booking-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 rounded-full bg-[hsl(80,60%,90%)] text-[hsl(80,70%,35%)] text-sm font-medium mb-4">
            Book Online
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-[hsl(80,70%,30%)]">
            Schedule Your Service
          </h2>
          <p className="mt-4 text-gray-600 max-w-2xl mx-auto">
            Pick a date and time that works for you. We'll confirm your appointment right away.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Calendar */}
          <Card className="bg-white border-0 shadow-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[hsl(80,70%,40%)]">
                <CalendarIcon className="w-5 h-5" />
                Select Date & Time
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                disabled={disabledDays}
                className="rounded-md border mx-auto"
                data-testid="booking-calendar"
              />
              
              {selectedDate && (
                <div className="mt-6">
                  <Label className="text-sm font-medium text-gray-700 mb-3 block">
                    Available Time Slots for {format(selectedDate, 'MMMM d, yyyy')}
                  </Label>
                  <div className="grid grid-cols-3 gap-2" data-testid="time-slots">
                    {timeSlots.map((slot) => (
                      <button
                        key={slot}
                        onClick={() => setSelectedTime(slot)}
                        disabled={bookedSlots.includes(slot)}
                        className={`time-slot ${selectedTime === slot ? 'selected' : ''}`}
                        data-testid={`time-slot-${slot.replace(/[:\s]/g, '-')}`}
                      >
                        {slot}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Booking Form */}
          <Card className="bg-white border-0 shadow-xl">
            <CardHeader>
              <CardTitle className="text-[hsl(80,70%,40%)]">Your Information</CardTitle>
              <CardDescription>Fill in your details to complete the booking</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="booking-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="booking-name">Full Name *</Label>
                    <Input
                      id="booking-name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="Your name"
                      required
                      data-testid="booking-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="booking-phone">Phone *</Label>
                    <Input
                      id="booking-phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="(xxx) xxx-xxxx"
                      required
                      data-testid="booking-phone-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="booking-email">Email *</Label>
                  <Input
                    id="booking-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="your@email.com"
                    required
                    data-testid="booking-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="booking-address">Property Address *</Label>
                  <Input
                    id="booking-address"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    placeholder="123 Main St, City, TX"
                    required
                    data-testid="booking-address-input"
                  />
                </div>
                <div>
                  <Label htmlFor="booking-service">Service Type *</Label>
                  <Select 
                    value={formData.service_type} 
                    onValueChange={(value) => setFormData({...formData, service_type: value})}
                  >
                    <SelectTrigger data-testid="booking-service-select">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map((service) => (
                        <SelectItem key={service.id} value={service.id}>
                          {service.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="booking-notes">Additional Notes</Label>
                  <Textarea
                    id="booking-notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Any special instructions or details..."
                    rows={3}
                    data-testid="booking-notes-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-[hsl(80,70%,40%)] hover:bg-[hsl(80,70%,35%)] text-white rounded-full py-6"
                  disabled={isLoading || !selectedDate || !selectedTime}
                  data-testid="booking-submit-btn"
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <div className="spinner"></div>
                      Booking...
                    </span>
                  ) : (
                    <>
                      <Clock className="w-5 h-5 mr-2" />
                      Confirm Booking
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

// Free Estimate Section
const EstimateSection = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    service_type: "",
    property_size: "",
    message: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await axios.post(`${API}/estimates`, formData);
      toast.success("Estimate request submitted! We'll contact you within 24 hours.");
      setFormData({ name: "", email: "", phone: "", address: "", service_type: "", property_size: "", message: "" });
    } catch (error) {
      toast.error("Failed to submit request. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="estimate" className="py-24 section-white" data-testid="estimate-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <span className="inline-block px-4 py-2 rounded-full bg-[hsl(30,60%,90%)] text-[hsl(30,60%,35%)] text-sm font-medium mb-4">
              Free Estimate
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[hsl(80,70%,30%)] mb-6">
              Get Your Free Quote Today
            </h2>
            <p className="text-gray-600 mb-8">
              Fill out the form and we'll provide you with a detailed, no-obligation estimate 
              for your lawn care needs. We typically respond within 24 hours.
            </p>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[hsl(80,60%,90%)] flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-6 h-6 text-[hsl(80,70%,35%)]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[hsl(80,70%,40%)]">No Obligation</h3>
                  <p className="text-gray-600 text-sm">Get a quote with zero commitment required.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[hsl(80,60%,90%)] flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-[hsl(80,70%,35%)]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[hsl(80,70%,40%)]">Quick Response</h3>
                  <p className="text-gray-600 text-sm">We'll get back to you within 24 hours.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[hsl(80,60%,90%)] flex items-center justify-center flex-shrink-0">
                  <Star className="w-6 h-6 text-[hsl(80,70%,35%)]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[hsl(80,70%,40%)]">Competitive Pricing</h3>
                  <p className="text-gray-600 text-sm">Fair prices for professional quality service.</p>
                </div>
              </div>
            </div>
          </div>

          <Card className="bg-white border-0 shadow-xl">
            <CardHeader>
              <CardTitle className="text-[hsl(80,70%,40%)]">Request Your Estimate</CardTitle>
              <CardDescription>Tell us about your property and needs</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="estimate-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="estimate-name">Full Name *</Label>
                    <Input
                      id="estimate-name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="Your name"
                      required
                      data-testid="estimate-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="estimate-phone">Phone *</Label>
                    <Input
                      id="estimate-phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="(xxx) xxx-xxxx"
                      required
                      data-testid="estimate-phone-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="estimate-email">Email *</Label>
                  <Input
                    id="estimate-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="your@email.com"
                    required
                    data-testid="estimate-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="estimate-address">Property Address *</Label>
                  <Input
                    id="estimate-address"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    placeholder="123 Main St, City, TX"
                    required
                    data-testid="estimate-address-input"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="estimate-service">Service Needed *</Label>
                    <Select 
                      value={formData.service_type} 
                      onValueChange={(value) => setFormData({...formData, service_type: value})}
                    >
                      <SelectTrigger data-testid="estimate-service-select">
                        <SelectValue placeholder="Select service" />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map((service) => (
                          <SelectItem key={service.id} value={service.id}>
                            {service.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="estimate-size">Property Size</Label>
                    <Select 
                      value={formData.property_size} 
                      onValueChange={(value) => setFormData({...formData, property_size: value})}
                    >
                      <SelectTrigger data-testid="estimate-size-select">
                        <SelectValue placeholder="Select size" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="small">Small (under 1/4 acre)</SelectItem>
                        <SelectItem value="medium">Medium (1/4 - 1/2 acre)</SelectItem>
                        <SelectItem value="large">Large (1/2 - 1 acre)</SelectItem>
                        <SelectItem value="xlarge">Extra Large (1+ acre)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="estimate-message">Additional Details</Label>
                  <Textarea
                    id="estimate-message"
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    placeholder="Tell us about your lawn, any specific concerns, or what you're looking for..."
                    rows={4}
                    data-testid="estimate-message-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-[hsl(30,60%,50%)] hover:bg-[hsl(30,60%,45%)] text-white rounded-full py-6 text-lg font-semibold"
                  disabled={isLoading}
                  data-testid="estimate-submit-btn"
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <div className="spinner"></div>
                      Submitting...
                    </span>
                  ) : (
                    "Get My Free Estimate"
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  return (
    <section className="py-24 section-cream" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 rounded-full bg-[hsl(80,60%,90%)] text-[hsl(80,70%,35%)] text-sm font-medium mb-4">
            Testimonials
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-[hsl(80,70%,30%)]">
            What Our Clients Say
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <Card 
              key={index} 
              className="testimonial-card bg-white border-0 shadow-lg"
              data-testid={`testimonial-card-${index}`}
            >
              <CardContent className="pt-12 pb-6">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-[hsl(45,90%,50%)] text-[hsl(45,90%,50%)]" />
                  ))}
                </div>
                <p className="text-gray-600 mb-6 italic">"{testimonial.text}"</p>
                <div className="border-t pt-4">
                  <div className="font-semibold text-[hsl(80,70%,40%)]">{testimonial.name}</div>
                  <div className="text-sm text-gray-500">{testimonial.location}</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  return (
    <section id="contact" className="py-24 section-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <span className="inline-block px-4 py-2 rounded-full bg-[hsl(80,60%,90%)] text-[hsl(80,70%,35%)] text-sm font-medium mb-4">
              Contact Us
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[hsl(80,70%,30%)] mb-6">
              Get In Touch
            </h2>
            <p className="text-gray-600 mb-8">
              Have questions or ready to get started? Reach out to us anytime. 
              We're here to help with all your lawn care needs.
            </p>

            <div className="space-y-6">
              <a 
                href="tel:9402328411" 
                className="flex items-center gap-4 p-4 rounded-xl bg-[hsl(80,60%,95%)] hover:bg-[hsl(80,60%,90%)] transition-colors"
                data-testid="contact-phone"
              >
                <div className="w-14 h-14 rounded-full bg-[hsl(80,70%,40%)] flex items-center justify-center">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="text-sm text-gray-500">Call or Text</div>
                  <div className="font-bold text-xl text-[hsl(80,70%,40%)]">(940) 232-8411</div>
                </div>
              </a>

              <div className="flex items-center gap-4 p-4 rounded-xl bg-gray-50">
                <div className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-gray-600" />
                </div>
                <div>
                  <div className="text-sm text-gray-500">Email Us</div>
                  <div className="font-semibold text-gray-800">t.vlawncareco@yahoo.com</div>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 rounded-xl bg-gray-50">
                <div className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-gray-600" />
                </div>
                <div>
                  <div className="text-sm text-gray-500">Service Area</div>
                  <div className="font-semibold text-gray-800">Wichita Falls, TX</div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[hsl(80,70%,30%)] rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">Ready to Transform Your Lawn?</h3>
            <p className="text-white/80 mb-8">
              Contact Emmanuel Villegas today for a free consultation and see how T&V Lawn Care Co. 
              can make your outdoor space shine.
            </p>
            <div className="space-y-4">
              <a href="#estimate">
                <Button 
                  size="lg" 
                  className="w-full bg-[hsl(30,60%,50%)] hover:bg-[hsl(30,60%,45%)] text-white rounded-full"
                  data-testid="contact-estimate-btn"
                >
                  Get Free Estimate
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
              <a href="tel:9402328411">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="w-full border-white/30 text-white hover:bg-white/10 rounded-full mt-3"
                  data-testid="contact-call-btn"
                >
                  <Phone className="mr-2 w-5 h-5" />
                  Call Now
                </Button>
              </a>
            </div>
            
            <div className="mt-8 pt-8 border-t border-white/20">
              <div className="text-white/60 text-sm">Business Hours</div>
              <div className="mt-2 space-y-1 text-white/90">
                <div>Monday - Friday: 7:00 AM - 6:00 PM</div>
                <div>Saturday: 8:00 AM - 4:00 PM</div>
                <div>Sunday: Closed</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="footer-pattern text-white py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-12">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_grass-quotes/artifacts/5ya1xfn4_DCCAD81D-0CE1-41B9-A712-38D6B35E8986.png" 
                alt="T&V Lawn Care Co. Logo"
                className="h-16 w-auto"
              />
            </div>
            <p className="text-white/70 max-w-md">
              Professional lawn care services for residential and commercial properties. 
              Dedicated to making your outdoor spaces beautiful.
            </p>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2 text-white/70">
              <li><a href="#services" className="hover:text-white transition-colors">Services</a></li>
              <li><a href="#about" className="hover:text-white transition-colors">About Us</a></li>
              <li><a href="#booking" className="hover:text-white transition-colors">Book Online</a></li>
              <li><a href="#estimate" className="hover:text-white transition-colors">Free Estimate</a></li>
              <li><a href="#contact" className="hover:text-white transition-colors">Contact</a></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4">Contact</h4>
            <ul className="space-y-2 text-white/70">
              <li>
                <a href="tel:9402328411" className="hover:text-white transition-colors">
                  (940) 232-8411
                </a>
              </li>
              <li>t.vlawncareco@yahoo.com</li>
              <li>Wichita Falls, TX</li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-white/10 mt-12 pt-8 text-center text-white/50 text-sm">
          <p>&copy; {new Date().getFullYear()} T&V Lawn Care Co. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

// Main App
function App() {
  return (
    <div className="App" data-testid="app-container">
      <Toaster position="top-right" richColors />
      <Header />
      <main>
        <HeroSection />
        <ServicesSection />
        <AboutSection />
        <BookingSection />
        <EstimateSection />
        <TestimonialsSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
